import { itemPriceParts, type PublicItem } from "@fusiqo/vozqi-shared";
import type { ReactNode } from "react";
import { priceLabel, variantPrice } from "./share-view";

/**
 * WHAT THE ITEM IS AND WHAT IT COSTS: its name as the page's title,
 * the price -- «Desde» the cheapest until an option is chosen, when the
 * options' prices differ --, the options each with its own price, the one
 * thing to do, then what the business wrote of it and, last, whose it is.
 * On a wide screen this is the panel beside the photos, and it follows the
 * reader down.
 */
export function ItemDetails({
  view,
  chosen,
  onChoose,
  actions,
  business,
}: {
  view: PublicItem;
  /** The option chosen; null until one is, while there are several. */
  chosen: number | null;
  onChoose: (index: number) => void;
  actions: ReactNode;
  /** The business's trade and day, once its profile is read; null without one. */
  business: ReactNode;
}) {
  const { variants, description } = view.item;
  const { from, price, unit } = itemPriceParts(view, chosen);
  const about = description?.trim() ?? "";
  return (
    <section className="item-details" aria-labelledby="item-name">
      <h1 id="item-name" className="item-name">
        {view.item.name}
      </h1>
      {price === "" ? null : (
        <p className="item-price" aria-live="polite" aria-label={priceLabel(view, chosen)}>
          {from ? <span className="item-price-from">Desde </span> : null}
          <span className="item-price-amount tabular">{price}</span>
          {unit === null ? null : <span className="item-price-unit"> / {unit}</span>}
        </p>
      )}

      {variants.length > 1 && (
        <div className="item-options" role="radiogroup" aria-label="Opciones">
          {variants.map((variant, index) => (
            <button
              key={index}
              type="button"
              role="radio"
              aria-checked={index === chosen}
              className="item-option"
              onClick={() => onChoose(index)}
            >
              <span className="item-option-name">{variant.name ?? `Opción ${String(index + 1)}`}</span>
              <span className="item-option-price tabular">{variantPrice(view, index)}</span>
            </button>
          ))}
        </div>
      )}

      {actions}

      {about === "" ? null : <p className="item-description">{about}</p>}
      {business}
    </section>
  );
}

/** The panel while the item loads. */
export function ItemDetailsSkeleton() {
  return (
    <section className="item-details" aria-hidden="true">
      <span className="share-skeleton share-skeleton--title" />
      <span className="share-skeleton share-skeleton--line" style={{ width: "35%" }} />
    </section>
  );
}
